import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../../services/api';
import { Filter, User, Trophy, UserPlus, Receipt } from 'lucide-react';
import { ACCENT, Spinner } from './shared';

export function OperatorsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyAgo);
  const [dateTo, setDateTo] = useState(today);
  const [staffId, setStaffId] = useState('');
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (staffId) params.set('staff_id', staffId);
      const res = await adminApi.get(`/admin/analytics/detailed?${params}`);
      setData(res.data);
    } catch {} finally { setLoading(false); }
  }, [dateFrom, dateTo, staffId]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading && !data) return <Spinner />;
  const ops = data?.staff_stats?.operators || [];

  // Esclude "Sistema" dalla classifica (operatore tecnico, non gareggia).
  const humanOps = ops.filter(o => o.id !== 'system' && o.name !== 'Sistema');
  const topByPoints = [...humanOps].sort((a, b) => (b.points_assigned || 0) - (a.points_assigned || 0)).slice(0, 3);
  const topRecruiter = [...humanOps].sort((a, b) => (b.customers_registered || 0) - (a.customers_registered || 0))[0];
  const topServer = [...humanOps].sort((a, b) => (b.customers_served || 0) - (a.customers_served || 0))[0];
  const topTicket = [...humanOps].sort((a, b) => (b.avg_amount_eur || 0) - (a.avg_amount_eur || 0))[0];
  const podiumStyles = [
    { bg: 'bg-gradient-to-br from-amber-50 to-amber-100/60', border: 'border-amber-300', text: 'text-amber-700', medal: '🥇', label: '1°' },
    { bg: 'bg-gradient-to-br from-slate-50 to-slate-100/60', border: 'border-slate-300', text: 'text-slate-600', medal: '🥈', label: '2°' },
    { bg: 'bg-gradient-to-br from-orange-50 to-orange-100/60', border: 'border-orange-300', text: 'text-orange-700', medal: '🥉', label: '3°' },
  ];
  const hasAnyPodium = topByPoints.some(o => (o.points_assigned || 0) > 0);
  const hasAnyMvp = (topRecruiter?.customers_registered || 0) > 0
    || (topServer?.customers_served || 0) > 0
    || (topTicket?.avg_amount_eur || 0) > 0;

  return (
    <div className="space-y-5" data-testid="operators-tab">
      <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid="operators-filters">
        <div className="flex items-center gap-2 mb-3"><Filter size={14} className="text-[#D44E20]" /><span className="text-xs text-[#64748B] font-medium">Filtri</span></div>
        <div className="flex flex-wrap gap-3 items-end">
          <div><label className="text-[10px] text-[#64748B] block mb-1">Da</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" /></div>
          <div><label className="text-[10px] text-[#64748B] block mb-1">A</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" /></div>
          {data?.staff_options && <div><label className="text-[10px] text-[#64748B] block mb-1">Operatore</label><select value={staffId} onChange={e => setStaffId(e.target.value)}
            className="h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none">
            <option value="">Tutti</option>{data.staff_options.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>}
          <button onClick={fetchData} className="h-9 px-4 bg-[#D44E20] text-white text-sm font-medium rounded-xl hover:opacity-90">Applica</button>
        </div>
      </div>

      {(hasAnyPodium || hasAnyMvp) && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="operators-leaderboard">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} className="text-amber-500" />
            <h3 className="font-semibold text-[#1A1A1A]">Classifica del periodo</h3>
          </div>

          {hasAnyPodium && (
            <>
              <p className="text-[11px] uppercase tracking-wider text-[#64748B] font-medium mb-2">Top 3 per punti assegnati</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                {topByPoints.map((op, i) => {
                  if ((op.points_assigned || 0) === 0) return null;
                  const s = podiumStyles[i];
                  return (
                    <div key={op.id} className={`${s.bg} ${s.border} border rounded-xl p-4`} data-testid={`podium-${i + 1}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xl" aria-hidden>{s.medal}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${s.text}`}>{s.label} posto</span>
                      </div>
                      <p className="text-lg font-bold text-[#1A1A1A] truncate" title={op.name}>{op.name}</p>
                      <div className="mt-2 flex items-baseline gap-1">
                        <p className={`text-2xl font-bold tabular-nums ${s.text}`}>{op.points_assigned.toLocaleString('it-IT')}</p>
                        <p className="text-[11px] text-[#64748B]">punti</p>
                      </div>
                      <p className="text-[11px] text-[#64748B] mt-1">{op.transactions} txn · {op.customers_served} clienti</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {hasAnyMvp && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(topRecruiter?.customers_registered || 0) > 0 && (
                <div className="flex items-center gap-3 bg-[#9333EA]/5 border border-[#9333EA]/20 rounded-xl p-3" data-testid="mvp-recruiter">
                  <div className="w-9 h-9 rounded-full bg-[#9333EA]/10 flex items-center justify-center shrink-0">
                    <UserPlus size={16} className="text-[#9333EA]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#9333EA] font-semibold">MVP iscrizioni</p>
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate" title={topRecruiter.name}>{topRecruiter.name}</p>
                    <p className="text-[11px] text-[#64748B]">{topRecruiter.customers_registered} nuovi iscritti</p>
                  </div>
                </div>
              )}
              {(topServer?.customers_served || 0) > 0 && (
                <div className="flex items-center gap-3 bg-[#2196F3]/5 border border-[#2196F3]/20 rounded-xl p-3" data-testid="mvp-server">
                  <div className="w-9 h-9 rounded-full bg-[#2196F3]/10 flex items-center justify-center shrink-0">
                    <User size={16} className="text-[#2196F3]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#2196F3] font-semibold">MVP servizio</p>
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate" title={topServer.name}>{topServer.name}</p>
                    <p className="text-[11px] text-[#64748B]">{topServer.customers_served} clienti serviti</p>
                  </div>
                </div>
              )}
              {(topTicket?.avg_amount_eur || 0) > 0 && (
                <div className="flex items-center gap-3 bg-[#3ECF8E]/5 border border-[#3ECF8E]/20 rounded-xl p-3" data-testid="mvp-ticket">
                  <div className="w-9 h-9 rounded-full bg-[#3ECF8E]/10 flex items-center justify-center shrink-0">
                    <Receipt size={16} className="text-[#3ECF8E]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#3ECF8E] font-semibold">MVP scontrino</p>
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate" title={topTicket.name}>{topTicket.name}</p>
                    <p className="text-[11px] text-[#64748B]">€ {topTicket.avg_amount_eur.toFixed(2)} medio</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ops.map(op => (
          <div key={op.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`operator-card-${op.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D44E20]/10 rounded-full flex items-center justify-center"><User size={18} className="text-[#D44E20]" /></div>
              <div><p className="font-semibold text-[#1A1A1A]">{op.name}</p><p className="text-[10px] text-[#64748B]">Operatore</p></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xl font-bold text-[#D44E20] tabular-nums">{op.points_assigned.toLocaleString('it-IT')}</p><p className="text-[10px] text-[#64748B]">Punti assegnati</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xl font-bold text-[#1A1A1A] tabular-nums">{op.transactions}</p><p className="text-[10px] text-[#64748B]">Transazioni</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xl font-bold text-[#3ECF8E] tabular-nums">{op.rewards_redeemed}</p><p className="text-[10px] text-[#64748B]">Premi dati</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center"><p className="text-xl font-bold text-[#2196F3] tabular-nums">{op.customers_served}</p><p className="text-[10px] text-[#64748B]">Clienti serviti</p></div>
              <div className="bg-gray-50 rounded-xl p-3 text-center col-span-2" data-testid={`op-${op.id}-registered`}><p className="text-xl font-bold text-[#9333EA] tabular-nums">{(op.customers_registered ?? 0).toLocaleString('it-IT')}</p><p className="text-[10px] text-[#64748B]">Iscritti registrati</p></div>
            </div>
            <div className="mt-3 flex justify-between text-xs text-[#64748B]">
              <span>Media: <strong className="text-[#1A1A1A]">{op.avg_points_per_tx} pt/txn</strong></span>
              {op.avg_amount_eur > 0 && <span>Scontrino: <strong className="text-[#1A1A1A]">{op.avg_amount_eur.toFixed(2)} EUR</strong></span>}
            </div>
          </div>
        ))}
        {ops.length === 0 && <p className="text-[#64748B] text-sm col-span-full text-center py-8">Nessun dato</p>}
      </div>
    </div>
  );
}
